import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { BehaviorSubject, Observable, Subscription, EMPTY } from "rxjs";
import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  FieldType,
  FilterEvent,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { SiteDTO } from "src/app/models/Sites/siteDTO";
import { SitesService } from "src/app/shared/services/sites/sites.service";
import { skip, map, catchError, finalize, tap, take } from 'rxjs/operators';
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { LinkSiteToProductsDTO } from "src/app/models/Products/productDTO";
import { ProductService } from "src/app/shared/services/products/products/product.service";
import { getColorByBgColor, getColorFromStr } from "src/app/shared/utils/colorUtils";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateSitesList } from "src/app/store/reducers";
import { SiteGroupsService } from "src/app/shared/services/site-groups/site-groups.service";
import { SiteGroupDTO } from "src/app/models/Sites/siteGroupDTO";
import { FailedProductsDialogComponent } from "src/app/layout-modules/sites-Management/store-site-products/dialog/failed-products-dialog/failed-products-dialog.component";
import { FailedLinkProductSitesDialogComponent } from "../failed-link-product-sites-dialog/failed-link-product-sites-dialog.component";

export interface LinkResult {
  errors: { [siteCode: string]: {
      [productCode: string]: {
        code: string,
        message: string,
      }[]
    } 
  }
}

const SITES_USAGE_CODE = "PRODUCT_SELECT_SITES";
const SITE_GROUPS_USAGE_CODE = "PRODUCT_SELECT_SITE_GROUPS";

@Component({
  selector: "app-product-sites-dialog",
  templateUrl: "./product-sites-dialog.component.html",
  styleUrls: ["./product-sites-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,  
})
export class ProductSitesDialogComponent implements OnInit, OnDestroy {
  public isLoading$ = new BehaviorSubject<boolean>(false);
  private loadingCounter = 0;

  public sitesColumnInfo$: Observable<ColumnSorterInfo[]>;
  public siteGroupsColumnInfo$: Observable<ColumnSorterInfo[]>;

  public sitesList: SiteDTO[] = [];
  public totalSites: number;
  public totalSiteGroups: number;

  private loadedSites$: Observable<SiteDTO[]>;
  private allSites: SiteDTO[];

  public siteGroupsList: SiteGroupDTO[];

  private sitesSub: Subscription;
  private siteGroupsListSub: Subscription;

  private productCode: string;

  private initialSelectedSites: Set<string>;
  private selectedSites: Set<string>;
  private sitesByGroup = new Map<string, Set<string>>();
  public initialSelectedGroups: string[] = [];
  private selectedGroups: Set<string>;
  private changedLinkSites = new Map<string, boolean>();

  public sitesTableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "SiteManagement.Code",
      field: "code",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
    },
    {
      header: "SiteManagement.SiteNumber",
      field: "siteNumber",
      fieldType: FieldType.STRING,
      searchable: true,
      style: "font-weight: 600",
    },
    {
      header: "SiteManagement.Description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.RegionDescription",
      field: "region.description",
      fieldType: FieldType.CHIP_STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.Profile",
      field: "siteProfile.description",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "SiteManagement.Enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "SiteManagement.ledSite",
      field: "ledSite",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "SiteGroup.siteGroup",
      field: "siteGroup.description",
      fieldType: FieldType.STRING,
    },
    {
      header: "SiteManagement.Created",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "SiteManagement.Updated",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "SiteManagement.comments",
      field: "comment",
      fieldType: FieldType.STRING,
      hidden: true,
    },
  ];

  public siteGroupsTableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Entity.token",
      field: "token",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
    },
    {
      header: "Tax.code",
      field: "code",
      fieldType: FieldType.STRING,
    },
    {
      header: "Tax.description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Tax.enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "Tax.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME
    },
    {
      header: "Tax.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      productCode: string;
      selectedSites: Set<string>;
    },
    public dialogRef: MatDialogRef<ProductSitesDialogComponent>,
    private sitesService: SitesService,
    private tableConfigService: TableConfigService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private productService: ProductService,
    private siteGroupsService: SiteGroupsService,
    private store: Store<AppState>,
    private dialog: MatDialog,
  ) {
    this.loadedSites$ = this.store.pipe(select(sitesStateSitesList));
  }

  ngOnInit(): void {
    this.getSitesTableConfig();
    this.getSiteGroupsTableConfig();
    this.initializeState();
    this.getSites();
    this.getSiteGroupsList();
  }

  private getSitesTableConfig() {
    this.setLoading(true);
    this.sitesColumnInfo$ = this.tableConfigService
      .getConfigSettings(SITES_USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo,
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.LoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false)),
      );
  }

  private getSiteGroupsTableConfig() {
    this.setLoading(true);
    this.siteGroupsColumnInfo$ = this.tableConfigService
      .getConfigSettings(SITE_GROUPS_USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo,
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.LoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false)),
      );
  }

  private initializeState() {
    this.productCode = this.data.productCode;
    this.initialSelectedSites = new Set<string>(
      Array.from(this.data.selectedSites),
    );
    this.selectedSites = new Set<string>(Array.from(this.data.selectedSites));
    this.loadedSites$.pipe(take(1))
    .subscribe((sites) => {
      this.allSites = sites;
      this.sitesByGroup.clear;
      sites.forEach((site) => {
        const group = site.siteGroup?.token;
        if (!group) {
          return;
        }
        const groupSites = this.sitesByGroup.get(group) ?? new Set<string>();
        groupSites.add(site.code);
        this.sitesByGroup.set(group, groupSites);
      });
      this.sitesByGroup.forEach((sites, group) => {
        const sitesNotSelected = [];
        sites.forEach(site => {if (!this.initialSelectedSites.has(site)) {sitesNotSelected.push(site)}});
        if (sitesNotSelected.length === 0) {
          this.initialSelectedGroups.push(group);
        }
      });
      this.selectedGroups = new Set<string>(this.initialSelectedGroups);
    });
  }

  private getSites(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent,
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.setLoading(true);

    this.sitesSub = this.sitesService
      .getSites(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent,
      )
      .pipe(
        map(({ result }) => {
          this.totalSites = result.size;
          return result.items;
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Sites.loadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false)),
      )
      .subscribe((sites) => {
        const treatedSites = sites.map(site => this.setCustomSiteDetails(site));
        this.sitesList = treatedSites;
      });
  }

  private getSiteGroupsList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.setLoading(true);
    this.siteGroupsListSub = this.siteGroupsService
      .getSiteGroupsList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent
      )
      .pipe(
        map((result) => {
          const { items, size } = result;
          this.totalSiteGroups = size;
          return items;
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("SiteGroup.siteGroupsLoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      ).subscribe((result: SiteGroupDTO[]) => this.siteGroupsList = result);
  }

  public onGetSitesData(event: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = event;
    this.getSites(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  public onGetSiteGroupsData(event: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = event;
    this.getSiteGroupsList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  public onSaveSitesTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.setLoading(true);
    this.tableConfigService
      .saveConfigSettings({
        code: SITES_USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveSuccess"),
            type: SnackbarType.SUCCESS,
          }),
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveError"),
            type: SnackbarType.SUCCESS,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false)),
      )
      .subscribe();
  }
  
  public onSaveSiteGroupsTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.setLoading(true);
    this.tableConfigService
      .saveConfigSettings({
        code: SITE_GROUPS_USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveSuccess"),
            type: SnackbarType.SUCCESS,
          }),
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveError"),
            type: SnackbarType.SUCCESS,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false)),
      )
      .subscribe();
  }

  public onSitesSelectionChange(event) {
    this.changeSitesRow(event.code, event.checked);
  }

  public onSiteGroupsSelectionChange(event) {
    this.sitesByGroup
      .get(event.token)
      .forEach((site) => this.changeSitesRow(site, event.checked));
  }

  public selectAllChanged(event: MatCheckboxChange) {
    this.allSites.forEach((site) => {
      this.changeSitesRow(site.code, event.checked)
    });
  }

  get isSelectionChanged(): boolean {
    return this.changedLinkSites.size > 0;
  }

  public onConfirmClick(): void {
    const linkData: LinkSiteToProductsDTO[] = Array.from(
      this.changedLinkSites.entries(),
      ([code, linked]) => ({
        siteCode: code,
        productsToLinkToSite: {
          productCode: this.productCode,
          shouldBeLinked: linked,
        }
      }),
    );
    this.linkOrUnlinkProductSites(linkData);
    this.dialogRef.close({submitted: true});
  }

  get initialSelectedArray(): string[] {
    return Array.from(this.initialSelectedSites);
  }

  get selectedCodesArray(): string[] {
    return Array.from(this.selectedSites);
  }

  get isAllSelected(): boolean {
    const notSelected = this.allSites?.filter((site) => !this.selectedSites.has(site.code));
    return notSelected.length === 0;
  }

  get selectedGroupsArray(): string[] {
    return Array.from(this.selectedGroups ?? []);
  }

  private changeSitesRow(code: string, isSelected: boolean): void {
    const originallySelected = this.initialSelectedSites.has(code);

    if (originallySelected === isSelected) {
      this.changedLinkSites.delete(code);
    } else {
      this.changedLinkSites.set(code, isSelected);
    }

    if (isSelected) {
      this.selectedSites.add(code);
    } else {
      this.selectedSites.delete(code);
    }

    this.sitesByGroup.forEach((sites, group) => {
      const sitesNotSelected = [];
      sites.forEach(site => {if (!this.selectedSites.has(site)) {sitesNotSelected.push(site)}});
      if (sitesNotSelected.length === 0) {
        this.selectedGroups.add(group);
      } else {
        this.selectedGroups.delete(group);
      }
    });
  }

  
  setCustomSiteDetails(siteDetail: SiteDTO) {
    const customSiteDetail = {
      ...siteDetail,
    };

    var regionDescriptionData = null;
    if (siteDetail?.region?.description) {
      regionDescriptionData = {
        value: siteDetail.region.description,
        icon: "place",
        style:
          `background-color: ` +
          getColorFromStr(siteDetail.region?.code) +
          `; color: ` +
          getColorByBgColor(siteDetail.region?.code) +
          `;`,
      };
    }

    var siteProfileDescriptionData = null;
    if (siteDetail?.siteProfile?.description) {
      siteProfileDescriptionData = {
        value: siteDetail.siteProfile.description,
        //icon: "local_gas_station",
        style:
          `background-color: ` +
          getColorFromStr(siteDetail.siteProfile?.code) +
          `; color: ` +
          getColorByBgColor(siteDetail.siteProfile?.code) +
          `;`,
      };
    }

    return {
      ...customSiteDetail,
      region: {
        ...customSiteDetail.region,
        description: regionDescriptionData,
      },
      siteProfile: {
        ...customSiteDetail.siteProfile,
        description: siteProfileDescriptionData,
      },
    };
  }

  private linkOrUnlinkProductSites(linkData: LinkSiteToProductsDTO[]): void {
    this.isLoading$.next(true);
    this.productService
      .linkOrUnlinkSiteProducts(linkData)
      .pipe(
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant(error ?? "Products.productSitesLinkError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading$.next(false);
        }),
      )
      .subscribe(({result}) => {
        if (result.errors && Object.keys(result.errors).length) {
          this.openFailedToLinkProductsDialog(result);
        } else {
          this.snackbarService.show({
            message: this.translate.instant("Products.productSitesLinkSuccess"),
            type: SnackbarType.SUCCESS,
          });
        }
      });
  }

  openFailedToLinkProductsDialog(result: LinkResult) {
    const dialogData: MatDialogConfig = {
      width: '900px',
      panelClass: 'failed-link-sites-dialog',
      autoFocus: false,
      data: {
        result: result,
      },
    };

    this.dialog.open(FailedLinkProductSitesDialogComponent, dialogData);
  }

  private setLoading(loading: boolean) {
    this.loadingCounter += loading ? 1 : -1;
    this.isLoading$.next(this.loadingCounter > 0);
  }

  ngOnDestroy(): void {
    if (this.sitesSub) {
      this.sitesSub.unsubscribe();
    }
    if (this.siteGroupsListSub) {
      this.siteGroupsListSub.unsubscribe();
    }
  }
}
